import { useEffect, useRef, useState } from 'react';
import { ApiError, api, getToken, setPrefill } from '../lib/api';
import { loginAfterConfirm } from './signupHelpers';

/**
 * Pantalla pública /auth/confirm-email?token=XXX (v1.3.2). Auto-confirma el
 * email al montar (POST /auth/confirm-email). Montada fuera de AuthProvider.
 */
export function ConfirmEmailScreen() {
  const [state, setState] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMsg, setErrorMsg] = useState<string>('');
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return; // StrictMode monta 2x en dev — evitamos doble POST.
    ran.current = true;

    const token = new URLSearchParams(window.location.search).get('token') ?? '';
    if (!token) {
      setErrorMsg('Enlace inválido: falta el token de confirmación.');
      setState('error');
      return;
    }
    api<{ tenantSlug?: string | null; email?: string | null }>('/auth/confirm-email', { method: 'POST', body: { token } })
      .then((r) => {
        // Alta 2 (b): sin sesión → directo al login con negocio y correo ya escritos (el
        // correo sale del token, nunca de la URL; queda en el prefill de este navegador).
        const next = getToken() ? null : loginAfterConfirm(r?.tenantSlug, r?.email);
        if (next) {
          setPrefill(next.tenantSlug, next.email);
          window.location.replace(next.url);
          return;
        }
        setState('success');
      })
      .catch((err) => {
        setErrorMsg(
          err instanceof ApiError && err.status === 400
            ? 'Este enlace es inválido, expiró o ya fue usado.'
            : 'No se pudo confirmar el correo. Intenta de nuevo más tarde.',
        );
        setState('error');
      });
  }, []);

  const hasSession = !!getToken();

  return (
    <div className="min-h-screen grid place-items-center bg-[#FAFAF7] px-4">
      <div className="card-soft w-full max-w-sm rounded-2xl border border-slate-200/80 bg-white p-8 text-center">
        <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-slate-400">
          FluidSol
        </p>

        {state === 'loading' && (
          <p className="mt-4 text-sm text-slate-500">Confirmando tu correo…</p>
        )}

        {state === 'success' && (
          <div className="mt-3 space-y-4">
            <h1 className="font-serif text-2xl tracking-tight text-slate-900">
              ¡Email confirmado!
            </h1>
            <p className="text-sm text-slate-500">
              Tu dirección de correo quedó verificada.
            </p>
            <a
              href="/"
              className="block w-full rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-slate-800"
            >
              {hasSession ? 'Ir al dashboard' : 'Ir al inicio de sesión'}
            </a>
          </div>
        )}

        {state === 'error' && (
          <div className="mt-3 space-y-4">
            <h1 className="font-serif text-2xl tracking-tight text-slate-900">
              No pudimos confirmar
            </h1>
            <p className="alert-over rounded-xl px-3 py-2 text-xs">{errorMsg}</p>
            <a
              href="/"
              className="block w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
            >
              Ir al inicio
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

export default ConfirmEmailScreen;
